import { useRef, useState, useCallback } from "react";
import { RefreshCw } from "lucide-react";

const THRESHOLD = 70;
const MAX = 110;
const atTop = () => (document.scrollingElement || document.documentElement).scrollTop <= 0 && window.scrollY <= 0;

// Lightweight pull-to-refresh for iOS standalone PWAs (which have no native one).
// Pull down from the very top; a spinner follows the finger and fires onRefresh past the threshold.
export const PullToRefresh = ({ onRefresh, enabled = true, children }) => {
  const startY = useRef(null);
  const [dist, setDist] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const onTouchStart = useCallback((e) => {
    if (!enabled || refreshing) return;
    startY.current = atTop() ? e.touches[0].clientY : null;
  }, [enabled, refreshing]);

  const onTouchMove = useCallback((e) => {
    if (startY.current == null || refreshing) return;
    const dy = e.touches[0].clientY - startY.current;
    if (dy <= 0 || !atTop()) { setDist(0); return; }
    setDist(Math.min(MAX, dy * 0.5)); // resistance
  }, [refreshing]);

  const onTouchEnd = useCallback(async () => {
    if (startY.current == null) return;
    startY.current = null;
    if (dist >= THRESHOLD && !refreshing) {
      setRefreshing(true);
      setDist(52);
      try { await onRefresh?.(); } catch { /* ignore */ }
      setRefreshing(false);
    }
    setDist(0);
  }, [dist, refreshing, onRefresh]);

  if (!enabled) return children;

  const progress = Math.min(1, dist / THRESHOLD);
  const active = dist > 4 || refreshing;

  return (
    <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} className="relative" data-testid="pull-to-refresh">
      <div
        className="pointer-events-none absolute left-1/2 top-0 z-20 -translate-x-1/2"
        style={{ transform: `translate(-50%, ${dist - 40}px)`, opacity: active ? 1 : 0, transition: startY.current == null ? "transform 250ms ease, opacity 250ms ease" : "none" }}
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-700 bg-zinc-900/95 shadow-lg shadow-black/40 backdrop-blur">
          <RefreshCw
            className={`h-5 w-5 text-[#E0575B] ${refreshing ? "animate-spin" : ""}`}
            style={{ transform: refreshing ? undefined : `rotate(${progress * 270}deg)` }}
          />
        </div>
      </div>
      <div style={{ transform: `translateY(${dist}px)`, transition: startY.current == null ? "transform 250ms ease" : "none" }}>
        {children}
      </div>
    </div>
  );
};
